import { useGoogleDriveCache } from "@/lib/contexts/GoogleDriveCacheContext";
import type {
  MutationInvalidationVars,
  UseDriveBrowserParams,
} from "./google-drive-browser-types";

type DriveCache = ReturnType<typeof useGoogleDriveCache>;

export function buildAuthHeaders(token: string, json = true): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

export function buildBrowseQueryParams(params: UseDriveBrowserParams): URLSearchParams {
  const { parentId, driveId, action, email, isSecondAccount, pageToken, pageSize, query } = params;
  const queryParams = new URLSearchParams();
  if (parentId) queryParams.set("parentId", parentId);
  if (driveId) queryParams.set("driveId", driveId);
  if (action) queryParams.set("action", action);
  if (email) queryParams.set("email", email);
  if (isSecondAccount) queryParams.set("isSecondAccount", "true");
  if (pageToken) queryParams.set("pageToken", pageToken);
  if (pageSize) queryParams.set("pageSize", String(pageSize));
  if (query && query.trim()) queryParams.set("q", query.trim());
  return queryParams;
}

export async function parseDriveErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const errorData = await response.json();
    return errorData.error || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Shared post-mutation invalidation: list cache for the (parent, drive, account)
 * tuple, plus the drives-list cache if the mutation touched a shared drive.
 */
export function invalidateAfterMutation(
  cache: DriveCache,
  variables: MutationInvalidationVars
): void {
  cache.invalidatePattern({
    action: "list",
    parentId: variables.parentId,
    driveId: variables.driveId,
    isSecondAccount: variables.isSecondAccount,
  });

  if (variables.driveId) {
    cache.invalidatePattern({
      action: "drives",
      isSecondAccount: variables.isSecondAccount,
    });
  }
}
